import React, { useMemo, useState, useEffect } from "react";

interface DatePickerProps {
  selectedDate?: Date;
  onDateSelect: (date: Date) => void;
  disabledDates?: string[]; // YYYY-MM-DD format
  className?: string;
  disabled?: boolean;
}

const DatePicker: React.FC<DatePickerProps> = ({
  selectedDate,
  onDateSelect,
  disabledDates = [],
  className = "",
  disabled = false,
}) => {
  const today = new Date();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [internalSelected, setInternalSelected] = useState<Date | null>(() => {
    if (selectedDate) {
      const dateObj =
        selectedDate instanceof Date ? selectedDate : new Date(selectedDate);
      return !isNaN(dateObj.getTime()) ? dateObj : today;
    }
    return today;
  });

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  // Normalize disabled dates into a Set for fast lookup
  const disabledSet = useMemo(() => {
    return new Set(
      disabledDates.map((d) => {
        const date = new Date(d);
        return formatDate(date.getFullYear(), date.getMonth(), date.getDate());
      })
    );
  }, [disabledDates]);

  // Update internal selected date when selectedDate prop changes
  useEffect(() => {
    if (selectedDate) {
      // Ensure selectedDate is a Date object
      const dateObj =
        selectedDate instanceof Date ? selectedDate : new Date(selectedDate);

      // Check if the date is valid
      if (!isNaN(dateObj.getTime())) {
        setInternalSelected(dateObj);
        // Update currentDate to show the correct month/year when selectedDate changes
        setCurrentDate(new Date(dateObj.getFullYear(), dateObj.getMonth(), 1));
      }
    }
  }, [selectedDate]);

  // Auto-select today's date if no selectedDate is provided (only for create mode)
  useEffect(() => {
    if (!selectedDate) {
      onDateSelect(today);
    }
  }, [selectedDate, today]);

  function formatDate(year: number, month: number, day: number) {
    return `${year}-${String(month + 1).padStart(2, "0")}-${String(
      day
    ).padStart(2, "0")}`;
  }

  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const firstDayOfWeek = (firstDay.getDay() + 6) % 7; // Monday = 0
    const daysInMonth = lastDay.getDate();

    const days: (number | null)[] = [];
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }
    for (let d = 1; d <= daysInMonth; d++) {
      days.push(d);
    }
    return days;
  };

  const handlePrevMonth = () => {
    if (disabled) return;

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const newDate = new Date(year, month - 1, 1);

    // Prevent going before current month
    if (
      newDate.getFullYear() < today.getFullYear() ||
      (newDate.getFullYear() === today.getFullYear() &&
        newDate.getMonth() < today.getMonth())
    ) {
      return;
    }

    setCurrentDate(newDate);
  };

  const handleNextMonth = () => {
    if (disabled) return;

    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );
  };

  const handleDateClick = (day: number) => {
    if (disabled) return;

    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );

    const dateStr = formatDate(
      newDate.getFullYear(),
      newDate.getMonth(),
      newDate.getDate()
    );

    const isBeforeToday = newDate < today;
    const isDisabled = disabledSet.has(dateStr);

    if (isBeforeToday || isDisabled) return;
    setInternalSelected(newDate);
    onDateSelect(newDate);
  };

  const isSelectedDate = (day: number) => {
    if (
      !internalSelected ||
      !(internalSelected instanceof Date) ||
      isNaN(internalSelected.getTime())
    ) {
      return false;
    }
    return (
      internalSelected.getDate() === day &&
      internalSelected.getMonth() === currentDate.getMonth() &&
      internalSelected.getFullYear() === currentDate.getFullYear()
    );
  };
  return (
    <div className={`space-y-6 lg:space-y-5 ${className}`}>
      <p className="font-bold">Session Date</p>
      <div className="datapicker select-none">
        {/* Header */}
        <div className="datapicker-header">
          <button
            id="prevMonth"
            type="button"
            onClick={handlePrevMonth}
            className="datapicker-month-btn"
            disabled={
              disabled ||
              currentDate.getFullYear() < today.getFullYear() ||
              (currentDate.getFullYear() === today.getFullYear() &&
                currentDate.getMonth() <= today.getMonth())
            }
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.7487 20.34C17.1127 20.7544 17.0724 21.3852 16.6587 21.75C16.2443 22.114 15.6135 22.0737 15.2487 21.66L7.24873 12.66C6.91709 12.2825 6.91709 11.7175 7.24873 11.34L15.2487 2.34C15.4794 2.05366 15.8485 1.91698 16.21 1.98407C16.5715 2.05117 16.867 2.31123 16.9796 2.66126C17.0921 3.01129 17.0034 3.39484 16.7487 3.66L9.33873 12L16.7487 20.34Z"
                fill="#999999"
              />
            </svg>
          </button>

          <p id="currentMonth" className="datapicker-month">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </p>

          <button
            id="nextMonth"
            type="button"
            onClick={handleNextMonth}
            className="datapicker-month-btn"
            disabled={disabled}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M16.7487 12.66L8.74867 21.66C8.38391 22.0737 7.75307 22.114 7.33867 21.75C6.92497 21.3852 6.8847 20.7544 7.24867 20.34L14.6587 12L7.24867 3.66C6.91287 3.24308 6.96496 2.63566 7.36684 2.28201C7.76872 1.92835 8.37783 1.95391 8.74867 2.34L16.7487 11.34C17.0803 11.7175 17.0803 12.2825 16.7487 12.66Z" />
            </svg>
          </button>
        </div>

        {/* Weekdays */}
        <div className="datapicker-calendar">
          {dayNames.map((day) => (
            <div key={day} className="datapicker-day-name">
              {day}
            </div>
          ))}
        </div>

        {/* Days */}
        <div id="calendarDays" className="datapicker-days-grid">
          {generateCalendarDays().map((day, idx) => {
            if (!day) {
              return (
                <div key={idx} className="datapicker-day empty">
                  {/* empty slot */}
                </div>
              );
            }

            const dateStr = formatDate(
              currentDate.getFullYear(),
              currentDate.getMonth(),
              day
            );
            const dateObj = new Date(
              currentDate.getFullYear(),
              currentDate.getMonth(),
              day
            );

            const isBeforeToday = dateObj < today;
            const isDisabled = disabledSet.has(dateStr);

            const isUnavailable = isBeforeToday || isDisabled || disabled;

            return (
              <button
                key={idx}
                type="button"
                onClick={() => handleDateClick(day)}
                className={`datapicker-day-num ${
                  isUnavailable ? "disabled" : ""
                } ${isSelectedDate(day) ? "selected" : ""}`}
                disabled={isUnavailable}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DatePicker;
